import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Trash2, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_COLORS = {
  organic: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  paid: 'bg-blue-50 text-blue-700 border-blue-200',
  email: 'bg-purple-50 text-purple-700 border-purple-200',
  referral: 'bg-amber-50 text-amber-700 border-amber-200',
  offline: 'bg-slate-50 text-slate-700 border-slate-200',
};

export default function UTMDefaultsPage() {
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ platform: '', source: '', medium: '', description: '', category: 'organic', sort_order: 99 });

  const fetchDefaults = async () => {
    try {
      const data = await api('/api/utm/defaults');
      setDefaults(data);
    } catch (err) {
      toast.error('Failed to load UTM defaults');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDefaults(); }, []);

  const handleSave = async () => {
    try {
      if (editItem) {
        await api(`/api/utm/defaults/${editItem._id}`, { method: 'PUT', body: JSON.stringify(form) });
        toast.success('UTM default updated');
      } else {
        await api('/api/utm/defaults', { method: 'POST', body: JSON.stringify(form) });
        toast.success('UTM default created');
      }
      setDialogOpen(false);
      setEditItem(null);
      setForm({ platform: '', source: '', medium: '', description: '', category: 'organic', sort_order: 99 });
      fetchDefaults();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this UTM default?')) return;
    try {
      await api(`/api/utm/defaults/${id}`, { method: 'DELETE' });
      toast.success('Deleted');
      fetchDefaults();
    } catch (err) { toast.error(err.message); }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ platform: item.platform, source: item.source, medium: item.medium, description: item.description, category: item.category, sort_order: item.sort_order || 99 });
    setDialogOpen(true);
  };

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            UTM Defaults
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Standard UTM parameters used across all clients. These define the tracking sources.
          </p>
        </div>
        <Button onClick={() => { setEditItem(null); setForm({ platform: '', source: '', medium: '', description: '', category: 'organic', sort_order: 99 }); setDialogOpen(true); }} style={{ background: '#1f2a4d' }}>
          <Plus size={16} className="mr-2" /> Add UTM Source
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Platform</TableHead>
                <TableHead>utm_source</TableHead>
                <TableHead>utm_medium</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {defaults.map((d) => (
                <TableRow key={d._id}>
                  <TableCell className="font-medium">{d.platform}</TableCell>
                  <TableCell><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{d.source}</code></TableCell>
                  <TableCell><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{d.medium}</code></TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] capitalize ${CATEGORY_COLORS[d.category] || ''}`}>{d.category}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{d.description}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(d)}><Pencil size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(d._id)}><Trash2 size={13} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit UTM Default' : 'Add UTM Source'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-2">
              <Label>Platform Name</Label>
              <Input value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} placeholder="e.g. Facebook Ads" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>utm_source</Label>
                <Input value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} placeholder="e.g. facebook" />
              </div>
              <div className="space-y-2">
                <Label>utm_medium</Label>
                <Input value={form.medium} onChange={e => setForm({ ...form, medium: e.target.value })} placeholder="e.g. paid" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="organic">Organic</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What this source tracks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.platform || !form.source} style={{ background: '#1f2a4d' }}>
              {editItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
